import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Mic, 
  Video, 
  Users, 
  Target, 
  Brain, 
  Eye, 
  TrendingUp, 
  CheckCircle, 
  ArrowLeft,
  Upload,
  Play,
  BarChart3,
  MessageSquare
} from 'lucide-react';

const InterviewMagic = () => {
  const navigate = useNavigate();
  const [jobDescription, setJobDescription] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [interviewType, setInterviewType] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Job InterviewMagic™</h1>
                  <p className="text-sm text-muted-foreground">Ace Every Interview. Speak With Purpose. Get the Offer.</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-5xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full text-lg font-bold shadow-lg animate-pulse">
                    Coming Soon! February 15th, 2026 Release!
                  </div>
                </div>
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Interview{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Magic™
                    </span>
                  </h1>
                  
                  <div className="max-w-4xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      You've crafted the perfect resume. Now it's time to own the room — with a tailored interview experience that prepares you not just for any interview, but for your interview.
                    </p>
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Left Column - What Is InterviewMagic */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-6 h-6 text-primary" />
                What Is Job InterviewMagic™?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Job InterviewMagic™ is an interactive, AI-powered mock interview tool that doesn't just throw random questions at you — it:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm">Analyzes your target job description</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm">Predicts the most likely questions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm">Runs realistic 1-on-1 and panel interview simulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm">Evaluates your answers, tone, and body language</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm">Gives you detailed feedback on how to improve</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Whether you're prepping for an entry-level role or an executive board interview, this is the tool that makes you unshakable under pressure.
              </p>
            </CardContent>
          </Card>

          {/* Right Column - Interview Setup Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-6 h-6 text-primary" />
                Start Your Interview Prep
              </CardTitle>
              <CardDescription>
                Get started with a tailored mock interview session
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="job-url">Job URL (Optional)</Label>
                <Input
                  id="job-url"
                  placeholder="Paste the job posting URL here..."
                  value={jobUrl}
                  onChange={(e) => setJobUrl(e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="job-description">Job Description</Label>
                <Textarea
                  id="job-description"
                  placeholder="Paste the job description here or we'll extract it from the URL above..."
                  rows={6}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
              </div>

              <div>
                <Label>Interview Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button 
                    variant={interviewType === '1-on-1' ? 'default' : 'outline'}
                    className="flex items-center gap-2"
                    onClick={() => setInterviewType('1-on-1')}
                  >
                    <Video className="w-4 h-4" />
                    1-on-1
                  </Button>
                  <Button 
                    variant={interviewType === 'panel' ? 'default' : 'outline'}
                    className="flex items-center gap-2"
                    onClick={() => setInterviewType('panel')}
                  >
                    <Users className="w-4 h-4" />
                    Panel
                  </Button>
                </div>
              </div>

              <Button className="w-full" size="lg">
                <Play className="w-4 h-4 mr-2" />
                Start Interview Practice
              </Button>
            </CardContent>
                </Card>
              </div>

              {/* How It Works Section */}
              <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-center text-2xl mb-2">How It Works</CardTitle>
            <CardDescription className="text-center">
              Your complete interview preparation process in 5 simple steps
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">1. Upload Job Description</h3>
                <p className="text-sm text-muted-foreground">
                  InterviewMagic™ scans the job, company, and industry to generate a customized question bank.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">2. Choose Interview Type</h3>
                <p className="text-sm text-muted-foreground">
                  Select a 1-on-1 simulation, or go advanced with a multi-avatar panel setup.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">3. Answer Real Questions</h3>
                <p className="text-sm text-muted-foreground">
                  Face questions tailored to the actual role, company culture, and current industry trends.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">4. Get Real-Time Feedback</h3>
                <p className="text-sm text-muted-foreground">
                  AI assessment of eye contact, nervous habits, filler words, energy level, and answer structure.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">5. Receive Your Score</h3>
                <p className="text-sm text-muted-foreground">
                  Detailed breakdown of performance with custom suggestions and STAR framework tips.
                </p>
              </div>
            </div>
              </CardContent>
              </Card>

              {/* Why It's a Game-Changer Section */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-6 h-6 text-primary" />
                Why Job InterviewMagic™ Is a Game-Changer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Badge variant="secondary" className="mt-0.5">🎯</Badge>
                  <div>
                    <h4 className="font-semibold text-sm">Tailored Interview Prep</h4>
                    <p className="text-sm text-muted-foreground">No more one-size-fits-all advice. You prepare for this job, not just a job.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Badge variant="secondary" className="mt-0.5">🗣️</Badge>
                  <div>
                    <h4 className="font-semibold text-sm">AI Interviewers That See & Hear You</h4>
                    <p className="text-sm text-muted-foreground">Get feedback on how you speak, look, move, and present.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Badge variant="secondary" className="mt-0.5">📊</Badge>
                  <div>
                    <h4 className="font-semibold text-sm">Instant, Actionable Feedback</h4>
                    <p className="text-sm text-muted-foreground">Track your progress with real scores and pinpoint improvements.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Badge variant="secondary" className="mt-0.5">🧘</Badge>
                  <div>
                    <h4 className="font-semibold text-sm">Confidence On-Demand</h4>
                    <p className="text-sm text-muted-foreground">Learn how to manage nerves and tell your story with power.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-primary" />
                Stop Hoping. Start Mastering.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                Job interviews aren't about being perfect — they're about being prepared, present, and positioned as the solution.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm">Step into the room ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm">Know what they'll ask</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm">Leave them with no doubts</span>
                </div>
              </div>
              <Button className="w-full" size="lg">
                <Mic className="w-4 h-4 mr-2" />
                Try InterviewMagic™ Now
              </Button>
                </CardContent>
                </Card>
              </div>

              {/* Final CTA */}
              <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-center">
          <CardContent className="py-12">
            <h2 className="text-3xl font-bold mb-4">Turn Tailored Interview Prep Into Job Offers</h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              With Job InterviewMagic™, you won't just sound like the right fit — you'll become impossible to overlook.
            </p>
            <Button size="lg" variant="secondary" className="px-8">
              <Play className="w-5 h-5 mr-2" />
              Start Your Practice Session
            </Button>
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewMagic;